import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RECONCILIATION_CONFLICT_LABELS,
  hasAtLeastRole,
  type Rapprochement,
  type ReconciliationStatus,
} from '@kn/shared';
import { api } from '../api/client';
import { messageErreur } from '../api/error-message';
import { FiltreMultiple } from '../components/FiltreMultiple';
import { useAuth } from '../auth/auth-context';
import { useTitrePage } from '../hooks/useTitrePage';

const CLE = ['rapprochements'] as const;

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Les rapprochements manuels, tels qu'ils se relisent (règle 17).
 *
 * Un rapprochement **ne se supprime pas** : il s'annule, avec un auteur et un
 * motif, et les mouvements reviennent dans le Restant. Cet écran est l'endroit
 * où cela se fait — et surtout l'endroit où un **conflit** se voit.
 *
 * Un conflit n'est jamais résolu par la machine. Quand un import apporte plus
 * tard une contrepartie automatique à un mouvement déjà rapproché, ou qu'une
 * journée réimportée en retire un, le rapprochement est signalé et **les deux
 * issues sont offertes** : le conserver en disant pourquoi, ou l'annuler. Le
 * défaire en silence effacerait le jugement d'une personne au profit d'une
 * règle qui, justement, ne savait pas.
 */
export function RapprochementsPage(): ReactElement {
  useTitrePage('Historique des rapprochements');
  // Tous les types, retirés compris : l'historique garde ceux qui ne se
  // saisissent plus.
  const types = useQuery({
    queryKey: ['reconciliation-types', 'tous'],
    queryFn: () => api.reconciliationTypes(false),
    staleTime: 5 * 60 * 1000,
  });
  const { user } = useAuth();
  const peutAgir = user ? hasAtLeastRole(user.role, 'OPERATEUR') : false;
  const queryClient = useQueryClient();

  const [statut, setStatut] = useState<ReconciliationStatus | ''>('ACTIF');
  // Types choisis (OU) ; aucun = tous. Combinés au statut, et suivis par
  // l'export (lot 0.14.0).
  const [typesChoisis, setTypesChoisis] = useState<string[]>([]);
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: [...CLE, statut, typesChoisis],
    queryFn: () => api.rapprochements(statut === '' ? undefined : statut, typesChoisis),
  });

  const apres = (texte: string): void => {
    setMessage(texte);
    setErreur(null);
    void queryClient.invalidateQueries({ queryKey: CLE });
    void queryClient.invalidateQueries({ queryKey: ['restant'] });
  };

  const annulation = useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) =>
      api.annulerRapprochement(id, motif),
    onSuccess: (fait) =>
      apres(
        `Rapprochement annulé : ses ${fait.lignes.length} mouvement(s) reviennent dans le Restant.`,
      ),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const acquittement = useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => api.acquitterConflit(id, motif),
    onSuccess: () => apres('Conflit acquitté : le rapprochement est conservé, la raison est tracée.'),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const enCours = annulation.isPending || acquittement.isPending;
  const motif = (id: string): string => motifs[id] ?? '';
  const poserMotif = (id: string, valeur: string): void =>
    setMotifs((tous) => ({ ...tous, [id]: valeur }));

  function conflit(r: Rapprochement): ReactElement | null {
    if (r.conflit === 'AUCUN' || r.conflitAcquitteLe) return null;
    return (
      <div className="conflit" role="alert">
        <p className="conflit__titre">
          ⚠ {RECONCILIATION_CONFLICT_LABELS[r.conflit]}
          {r.conflitDetecteLe && <> — détecté le {formaterDate(r.conflitDetecteLe)}</>}
        </p>
        <p className="sous-titre">
          Deux issues, et c&apos;est vous qui tranchez : conserver ce rapprochement en disant
          pourquoi, ou l&apos;annuler — les mouvements reviendront dans le Restant.
        </p>
        {peutAgir && (
          <div className="barre-actions">
            <label className="champ champ--large" htmlFor={`motif-${r.id}`}>
              <span>Motif</span>
              <input
                id={`motif-${r.id}`}
                type="text"
                value={motif(r.id)}
                placeholder="Pourquoi conserver, ou pourquoi annuler"
                onChange={(e) => poserMotif(r.id, e.target.value)}
              />
            </label>
            <button
              type="button"
              className="bouton"
              disabled={enCours || motif(r.id).trim() === ''}
              onClick={() => acquittement.mutate({ id: r.id, motif: motif(r.id).trim() })}
            >
              Conserver malgré le conflit
            </button>
            <button
              type="button"
              className="bouton bouton--secondaire"
              disabled={enCours || motif(r.id).trim().length < 10}
              title="Le motif d'annulation doit être explicite (10 caractères au minimum)."
              onClick={() => annulation.mutate({ id: r.id, motif: motif(r.id).trim() })}
            >
              Annuler le rapprochement
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <h1>Historique des rapprochements</h1>
      <p className="sous-titre">
        Tout ce qui a été rapproché à la main depuis le Restant — <strong>actifs et
        annulés</strong>. Rapprocher <strong>explique</strong> : cela ne solde pas, les cumuls et
        les indicateurs ne bougent pas. Un rapprochement ne se supprime pas, il s&apos;annule.
      </p>

      {message && (
        <p className="alerte alerte--info" role="status">
          {message}
        </p>
      )}
      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}

      <div className="carte">
        <div className="barre-actions">
          <label className="champ" htmlFor="rappro-statut">
            <span>Statut</span>
            <select
              id="rappro-statut"
              value={statut}
              onChange={(e) => setStatut(e.target.value as ReconciliationStatus | '')}
            >
              <option value="ACTIF">Actifs</option>
              <option value="ANNULE">Annulés</option>
              <option value="">Tous</option>
            </select>
          </label>
          <FiltreMultiple
            titre="Types"
            options={(types.data ?? []).map((t) => ({
              valeur: t.id,
              libelle: t.actif ? t.libelle : `${t.libelle} (retiré)`,
            }))}
            choisis={typesChoisis}
            onChange={setTypesChoisis}
            libelleRecherche="Rechercher un type"
            placeholder="Libellé du type"
          />
          <a
            className="bouton bouton--secondaire"
            href={api.rapprochementsExportUrl(statut === '' ? undefined : statut, typesChoisis)}
          >
            Exporter (Excel)
          </a>
        </div>

        {liste.isPending && <p className="chargement">Chargement des rapprochements…</p>}
        {liste.isError && (
          <p className="alerte alerte--erreur" role="alert">
            {messageErreur(liste.error)}
          </p>
        )}

        {liste.data && liste.data.length === 0 && (
          <p className="alerte alerte--info" role="status">
            {typesChoisis.length > 0
              ? 'Aucun rapprochement de ces types à ce statut.'
              : 'Aucun rapprochement à ce statut.'}
          </p>
        )}

        {liste.data?.map((r) => (
          <article key={r.id} className="rapprochement">
            <header className="rapprochement__entete">
              <span className="rapprochement__type">
                <span className="rapprochement__type-libelle">Type</span>
                <strong>{r.typeLibelle}</strong>
              </span>
              {r.statut === 'ANNULE' && <span className="etiquette etiquette--inactif">Annulé</span>}
              {r.conflitAcquitteLe && (
                <span className="etiquette etiquette--attente">Conflit acquitté</span>
              )}
              <span className="rapprochement__meta">
                {formaterDate(r.createdAt)} — {r.auteur ?? 'auteur inconnu'}
              </span>
              <span className="rapprochement__meta">
                {r.lignes.length} mouvement(s)
                {Number(r.residuel) !== 0 && (
                  <>
                    {' '}
                    · résiduel <strong>{formaterNombre(r.residuel)}</strong> UVC
                  </>
                )}
              </span>
            </header>

            <p className="rapprochement__commentaire">{r.commentaire}</p>

            {r.statut === 'ANNULE' && (
              <p className="sous-titre">
                Annulé le {r.annuleLe ? formaterDate(r.annuleLe) : '—'} par {r.annulePar ?? '—'} :{' '}
                {r.motifAnnulation}
              </p>
            )}

            {conflit(r)}

            <table className="tableau">
              <thead>
                <tr>
                  <th scope="col">Article</th>
                  <th scope="col">Source</th>
                  <th scope="col">Jour</th>
                  <th scope="col">UVC au moment du geste</th>
                </tr>
              </thead>
              <tbody>
                {r.lignes.map((l) => (
                  <tr key={`${l.source}-${l.groupKey}`}>
                    <td>
                      <a href={`/ecarts/${l.codpro}`}>{l.codpro}</a>
                    </td>
                    <td>{l.source}</td>
                    <td>{l.jour.slice(0, 10).split('-').reverse().join('/')}</td>
                    <td className="droite">{formaterNombre(l.uvc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {peutAgir && r.statut === 'ACTIF' && r.conflit === 'AUCUN' && (
              <div className="barre-actions">
                <label className="champ champ--large" htmlFor={`annul-${r.id}`}>
                  <span>Motif d&apos;annulation</span>
                  <input
                    id={`annul-${r.id}`}
                    type="text"
                    value={motif(r.id)}
                    placeholder="Dix caractères au minimum : ce motif reste au dossier"
                    onChange={(e) => poserMotif(r.id, e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="bouton bouton--secondaire"
                  disabled={enCours || motif(r.id).trim().length < 10}
                  onClick={() => annulation.mutate({ id: r.id, motif: motif(r.id).trim() })}
                >
                  Annuler ce rapprochement
                </button>
              </div>
            )}
          </article>
        ))}
      </div>
    </>
  );
}
